// src/components/ControlTeamSelectAutocomplete.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, HStack, Image, InputGroup, Text } from '@chakra-ui/react';
import { Select, chakraComponents } from 'chakra-react-select';
import { Control, Controller, FieldPath, FieldValues, UseControllerProps } from 'react-hook-form';
import type { Club } from '@/model';
import { GetParamsObject } from '@/model/query-params';
import { useClub, useClubByIdStable } from '@/services/queries/club';


type BaseProps = {
  clubs?: Club[]; // opcional (seed inicial)
  width?: any;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  controlBg?: string;
  showFlag?: boolean;
  flagSize?: number;
  menuZIndex?: number;
  usePortal?: boolean;
  minChars?: number;
  pageSize?: number;
  prefetchOnOpen?: boolean;
};

type ControlProps<T extends FieldValues> = Pick<UseControllerProps<T>, 'name' | 'control' | 'rules'>;

export type ControlTeamSelectAutocompleteProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = BaseProps &
  ControlProps<TFieldValues> & {
    name: TName;
    control: Control<TFieldValues>;
  };

export default function ControlTeamSelectAutocomplete<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  rules,
  clubs = [], // seed opcional
  width = '100%',
  label,
  placeholder = 'Selecione...',
  disabled,
  error,
  controlBg = 'white',
  showFlag = true,
  flagSize = 18,
  menuZIndex = 1800,
  usePortal = true,
  minChars = 2,
  pageSize = 20,
  prefetchOnOpen = true,
}: ControlTeamSelectAutocompleteProps<TFieldValues, TName>) {
  // ----- estado de UI / busca -----
  const [inputValue, setInputValue] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [menuOpen, setMenuOpen] = useState(false);

  // seed estável para evitar ressetar em todo render
  const seedRef = useRef<Club[] | null>(null);

  // items acumulados (resultado paginado)
  const [items, setItems] = useState<Club[]>(clubs || []);
  const [hasMore, setHasMore] = useState(true);

  // guarda o seed UMA vez
  useEffect(() => {
    if (!seedRef.current && clubs && clubs.length) {
      seedRef.current = clubs;
      setItems(clubs);
    }
  }, [clubs]);

  // debounce simples
  useEffect(() => {
    const id = setTimeout(() => setDebounced(inputValue.trim()), 300);
    return () => clearTimeout(id);
  }, [inputValue]);

  // ----- query remota por lista (filtra por name) -----
  const params = useMemo(
    () =>
      GetParamsObject(
        { name: debounced || '' },
        page,
        pageSize,
        'asc',
        'name',
      ),
    [debounced, page, pageSize],
  );

  // busca só com menu aberto e condição de minChars (ou prefetchOnOpen)
  const enabledList = menuOpen && (debounced.length >= minChars || prefetchOnOpen);
  const { data: listData, isFetching } = useClub(params, enabledList);

  // Mescla/atualiza itens quando chegarem resultados
  useEffect(() => {
    if (!enabledList || !listData) return;
    const next = listData.data || [];
    setHasMore(listData.meta ? page < listData.meta.pages : next.length === pageSize);

    setItems(prev => {
      if (page === 1) return next;
      const existing = new Set(prev.map(i => i.uuid));
      const merged = [...prev];
      next.forEach(n => {
        if (!existing.has(n.uuid)) merged.push(n);
      });
      return merged;
    });
  }, [listData, enabledList, page, pageSize]);

  // Reset paginação quando o termo mudar
  useEffect(() => {
    setPage(1);
    if (debounced.length < minChars && !(prefetchOnOpen && menuOpen)) {
      const seed = seedRef.current ?? [];
      // evita setState desnecessário
      setItems(prev => (prev === seed ? prev : seed));
      setHasMore(true);
    }
    // ⚠️ intencionalmente sem `clubs` nas deps
  }, [debounced, minChars, prefetchOnOpen, menuOpen]);

  // ----- busca do item selecionado por uuid (para exibir label/flag corretos) -----
  const SelectedById = ({ value }: { value?: string }) => {
    const { data } = useClubByIdStable(value || '');
    const club = data as Club | undefined;

    useEffect(() => {
      if (!club) return;
      setItems(prev => {
        if (prev.some(i => i.uuid === club.uuid)) return prev;
        return [club, ...prev];
      });
    }, [club]);

    return null;
  };

  // Mapeia itens -> options do select
  const options = useMemo(
    () =>
      items.map(c => ({
        value: c.uuid,
        label: c.name,
        logo: c.logo_path,
      })),
    [items],
  );

  // Components com bandeira/logo
  const FlagOption = (props: any) => {
    const { data } = props;
    return (
      <chakraComponents.Option {...props}>
        <HStack spacing={2}>
          {showFlag && data.logo ? (
            <Image alt={data.label} src={data.logo} boxSize={`${flagSize}px`} borderRadius='sm' objectFit='contain' />
          ) : null}
          <Text noOfLines={1}>{data.label}</Text>
        </HStack>
      </chakraComponents.Option>
    );
  };

  const FlagSingleValue = (props: any) => {
    const { data } = props;
    return (
      <chakraComponents.SingleValue {...props}>
        <HStack spacing={2}>
          {showFlag && data.logo ? (
            <Image alt={data.label} src={data.logo} boxSize={`${flagSize}px`} borderRadius='sm' objectFit='contain' />
          ) : null}
          <Text noOfLines={1}>{data.label}</Text>
        </HStack>
      </chakraComponents.SingleValue>
    );
  };

  const baseComponents = { IndicatorSeparator: () => null };
  const components = showFlag ? { ...baseComponents, Option: FlagOption, SingleValue: FlagSingleValue } : baseComponents;

  const menuPortalTarget = usePortal && typeof window !== 'undefined' ? document.body : undefined;
  const menuPosition = usePortal ? 'fixed' : 'absolute';

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => {
        const selected = options.find(o => o.value === (field.value as string)) ?? null;

        return (
          <Box w={width} position='relative' zIndex={usePortal ? 'auto' : menuZIndex}>
            {label ? (
              <Text fontWeight='medium' mb={1}>
                {label}
              </Text>
            ) : null}

            {/* Busca por ID apenas se há valor e ele AINDA não está nas opções */}
            {field.value && !selected ? <SelectedById value={field.value as string} /> : null}

            <InputGroup display='flex' flexDirection='column'>
              <Select
                isDisabled={disabled}
                isClearable
                placeholder={placeholder}
                options={options}
                value={selected}
                onChange={opt => field.onChange((opt as any)?.value ?? '')}
                onBlur={field.onBlur}
                openMenuOnFocus
                openMenuOnClick
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                menuShouldScrollIntoView={false}
                menuShouldBlockScroll={false}
                components={components}
                // --- handlers de busca remota/paginação ---
                onMenuOpen={() => {
                  setMenuOpen(true);
                  setPage(1);
                }}
                onMenuClose={() => setMenuOpen(false)}
                onInputChange={(val, meta) => {
                  if (meta.action === 'input-change') setInputValue(val);
                }}
                onMenuScrollToBottom={() => {
                  if (!isFetching && hasMore) setPage(p => p + 1);
                }}
                isLoading={isFetching}
                noOptionsMessage={() =>
                  debounced.length < minChars && !prefetchOnOpen
                    ? 'Digite para buscar...'
                    : 'Nenhum resultado'
                }
                loadingMessage={() => 'Carregando...'}
                chakraStyles={{
                  container: p => ({ ...p, w: '100%' }),
                  control: (p, state) => ({
                    ...p,
                    bg: controlBg,
                    border: 'none',
                    borderBottom: '1px solid',
                    borderColor: error ? 'red.500' : state.isFocused ? 'brand.500' : 'gray.300',
                    rounded: 0,
                    boxShadow: 'none !important',
                    minH: '36px',
                    h: '36px',
                    px: 0,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    position: 'relative',
                    zIndex: 1,
                  }),
                  valueContainer: p => ({ ...p, px: 0, minW: 0 }),
                  inputContainer: p => ({ ...p, px: 0 }),
                  indicatorsContainer: p => ({ ...p, pr: 0 }),
                  placeholder: p => ({ ...p, color: 'gray.500' }),
                  singleValue: p => ({ ...p, color: 'gray.800' }),
                  menuPortal: p => ({ ...p, zIndex: menuZIndex }),
                  menu: p => ({
                    ...p,
                    mt: 1,
                    bg: 'white',
                    borderRadius: 'md',
                    boxShadow: 'lg',
                    borderWidth: '1px',
                    borderColor: 'gray.200',
                    zIndex: menuZIndex,
                    position: 'absolute',
                  }),
                  menuList: p => ({
                    ...p,
                    bg: 'white',
                    maxH: '280px',
                    py: 1,
                  }),
                  option: (p, state) => ({
                    ...p,
                    bg: state.isSelected ? 'gray.100' : state.isFocused ? 'gray.50' : 'white',
                    color: 'gray.800',
                    cursor: 'pointer',
                    _active: { bg: 'gray.200' },
                  }),
                  dropdownIndicator: (p, state) => ({
                    ...p,
                    color: state.isFocused ? 'brand.500' : 'gray.500',
                    bg: 'white',
                    px: 1,
                  }),
                  clearIndicator: p => ({
                    ...p,
                    color: 'gray.500',
                    px: 1,
                    _hover: { color: 'gray.700' },
                  }),
                }}
              />
            </InputGroup>

            {error ? (
              <Text fontSize='14px' color='red.500' mt={2}>
                {error}
              </Text>
            ) : null}
          </Box>
        );
      }}
    />
  );
}
